import { ChangeDetectionStrategy, Component, EventEmitter, Input, type OnInit, Output, inject } from '@angular/core';
import { take } from 'rxjs/operators';
import type { AnalyticsSettingsFormGroupType } from '@common/types/analytics-setings.type';
import {
    EPixelTypes,
    type GoogleSettingsFormGroupType,
    type PixelSettingsFormGroupType,
} from '@common/types/analytics-setings.type';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ELanguages } from '@common/enums';
import { SettingsFacade } from '@web/store/website/settings/settings.facade';
import { NgIf } from '@angular/common';
import { SettingsBreadcrumbsComponent } from '../web-settings/settings-breadcrumbs/settings-breadcrumbs.component';
import { LetDirective } from '@ngrx/component';
import { SpSwitchComponent } from '@libs/ui/sp-switch/src/sp-switch.component';
import { GoogleSettingsComponent } from './google-settings/google-settings.component';
import { PixelSettingsComponent } from './pixel-settings/pixel-settings.component';

@Component({
    selector: 'web-analytics-settings',
    templateUrl: './web-analytics-settings.component.html',
    styleUrls: ['./web-analytics-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgIf,
        SettingsBreadcrumbsComponent,
        LetDirective,
        SpSwitchComponent,
        GoogleSettingsComponent,
        PixelSettingsComponent,
        TranslateModule,
    ],
})
export class WebAnalyticsSettingsComponent implements OnInit {
    private readonly siteFacade = inject(SiteFacade);
    private readonly settingsFacade = inject(SettingsFacade);
    private readonly translate = inject(TranslateService);

    @Input() public itemForm: AnalyticsSettingsFormGroupType;
    @Input() public pixelAnalyticType: EPixelTypes;
    @Input() public showFullSettings: boolean = true;
    @Input() public isPaymentForm: boolean;
    @Input() public isShopCart: boolean;
    @Output() public cancelEvent: EventEmitter<any> = new EventEmitter();

    public readonly websiteSettings$ = this.settingsFacade.siteSettings$;
    protected readonly pixelTypes = EPixelTypes;

    public isDisabledPixelAnalyticAfterPaymentControl: boolean = false;
    public isDisabledGoogleAnalyticAfterPaymentControl: boolean = false;

    ngOnInit() {
        this.websiteSettings$.pipe(take(1)).subscribe((siteSettings) => {
            if (!siteSettings.google_analytics) this.toggleGoogleAnalyticControl(false);
            if (!siteSettings.facebook_pixel) this.togglePixelAnalyticControl(false);

            if (!this.isShopCart) return;

            if (!siteSettings.google_api_secret) {
                this.toggleGoogleAnalyticAfterPaymentControl(false);
                this.isDisabledGoogleAnalyticAfterPaymentControl = true;
            }
            if (!siteSettings.facebook_access_token) {
                this.togglePixelAnalyticAfterPaymentControl(false);
                this.isDisabledPixelAnalyticAfterPaymentControl = true;
            }
        });
    }

    public onCancel(): void {
        this.cancelEvent.emit();
    }

    public toggleGoogleAnalyticControl(event: boolean) {
        this.googleAnalyticControl.patchValue(event);
    }

    public toggleGoogleAnalyticAfterPaymentControl(event: boolean) {
        this.googleAnalyticAfterPaymentControl.patchValue(event);
    }

    public togglePixelAnalyticControl(event: boolean) {
        this.pixelAnalyticControl.patchValue(event);
        if (event) {
            this.pixelTypeControl.patchValue(this.pixelAnalyticType);
        }
    }

    public togglePixelAnalyticAfterPaymentControl(event: boolean) {
        this.pixelAnalyticAfterPaymentControl.patchValue(event);
        if (event) {
            this.pixelTypeAfterPaymentControl.patchValue(this.pixelTypes.purchase);
        }
    }

    public get googleAnalyticControl() {
        return this.itemForm.get('googleAnalytic');
    }

    public get googleAnalyticAfterPaymentControl() {
        return this.itemForm.get('googleAnalyticAfterPayment');
    }

    public get pixelAnalyticControl() {
        return this.itemForm.get('pixelAnalytic');
    }

    public get pixelAnalyticAfterPaymentControl() {
        return this.itemForm.get('pixelAnalyticAfterPayment');
    }

    public get pixelTypeControl() {
        return this.pixelSettingsGroup.get('type');
    }

    public get pixelTypeAfterPaymentControl() {
        return this.pixelSettingsGroup.get('typeAfterPayment');
    }

    public get googleGoogleSettingsGroup(): GoogleSettingsFormGroupType {
        return this.itemForm.get('googleSettings') as GoogleSettingsFormGroupType;
    }

    public get pixelSettingsGroup(): PixelSettingsFormGroupType {
        return this.itemForm.get('pixelSettings') as PixelSettingsFormGroupType;
    }

    public get helpGALink(): string {
        return `https://sendpulse.com/${this.lang}knowledge-base/landing-page/statistics-and-analytics/connect-google-analytics`;
    }

    public get helpPixelLink(): string {
        return `https://sendpulse.com/${this.lang}knowledge-base/landing-page/statistics-and-analytics/connect-facebook-pixel`;
    }

    private get lang(): string {
        const languages = [ELanguages.RU, ELanguages.UA];

        if (this.translate.currentLang === ELanguages.PT_BR) return 'br/';

        return languages.includes(this.translate.currentLang as ELanguages) ? `${this.translate.currentLang}/` : '';
    }
}
